import React, { useCallback, useEffect, useState } from "react";
import { Container, Form, Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "../lib/config/firebaseInit";
import { fetchReferralSummary, applyReferralCode } from "../lib/webService/webService.jsx";
import { toast } from "../utilities/toast";
import { formatCurrency } from "../utilities/formatCurrency";
import {
  ArrowLeft,
  Clipboard,
  ClipboardCheck,
  Whatsapp,
  Wallet2,
  GiftFill,
  PersonPlusFill,
  BagCheckFill,
  CheckCircleFill,
} from "react-bootstrap-icons";
import { Seo } from "../components/Seo";
import { generateNoIndexSEO } from "../lib/seo/metaGenerators";

// ─── Constants ────────────────────────────────────────────────────────────────
// Display copy only — the amount actually credited is decided server-side in
// mzito-pay/src/services/referral.service.ts (REFERRER_REWARD / REFEREE_REWARD).
// Keep these in sync if that ever changes.
const REFERRER_REWARD = 100;
const REFEREE_REWARD = 100;

const FONT =
  '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Segoe UI", system-ui, sans-serif';

// ─── Shared atoms (mirrors Orders.jsx / Account.tsx) ───────────────────────────

function IconBtn({ onClick, children, tint = "rgba(60,60,67,0.08)" }) {
  return (
    <button
      onClick={onClick}
      style={{
        width: 36, height: 36, borderRadius: "50%",
        border: "none", background: tint,
        display: "flex", alignItems: "center", justifyContent: "center",
        cursor: "pointer", flexShrink: 0,
      }}
    >
      {children}
    </button>
  );
}

function Card({ children, style = {} }) {
  return (
    <div
      style={{
        backgroundColor: "rgba(255,255,255,0.88)",
        border: "1px solid rgba(15,23,42,0.06)",
        borderRadius: 16,
        padding: "18px 20px",
        marginBottom: 16,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

function SectionLabel({ children }) {
  return (
    <p
      style={{
        margin: "0 0 12px",
        fontSize: "0.72rem",
        fontWeight: 600,
        color: "rgba(60,60,67,0.55)",
        textTransform: "uppercase",
        letterSpacing: "0.05em",
      }}
    >
      {children}
    </p>
  );
}

function StatBlock({ value, label }) {
  return (
    <div style={{ flex: 1, textAlign: "center" }}>
      <div style={{ fontSize: "1.375rem", fontWeight: 700, color: "#1C1C1E", letterSpacing: "-0.02em" }}>
        {value}
      </div>
      <div style={{ fontSize: "0.75rem", color: "rgba(60,60,67,0.5)", marginTop: 2 }}>{label}</div>
    </div>
  );
}

function StepRow({ Icon, title, body, last }) {
  return (
    <div
      style={{
        display: "flex", gap: 12, alignItems: "flex-start",
        padding: "12px 0", borderBottom: last ? "none" : "1px solid rgba(60,60,67,0.07)",
      }}
    >
      <div
        style={{
          width: 34, height: 34, borderRadius: 10, flexShrink: 0,
          backgroundColor: "rgba(0,122,255,0.1)",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}
      >
        <Icon size={15} color="#007AFF" />
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: "0.875rem", fontWeight: 600, color: "#1C1C1E" }}>{title}</div>
        <div style={{ fontSize: "0.8rem", color: "rgba(60,60,67,0.55)", marginTop: 2, lineHeight: 1.4 }}>
          {body}
        </div>
      </div>
    </div>
  );
}

function SkeletonPage() {
  return (
    <Container className="py-4" style={{ maxWidth: 600 }}>
      <div style={{ display: "flex", justifyContent: "center", padding: "80px 0" }}>
        <Spinner animation="border" style={{ width: 26, height: 26, borderWidth: 2, color: "#007AFF" }} />
      </div>
    </Container>
  );
}

// ─── Referrals ────────────────────────────────────────────────────────────────

const Referrals = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [codeInput, setCodeInput] = useState("");
  const [isApplying, setIsApplying] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (u) => {
      setUser(u);
      setAuthLoading(false);
      if (!u) navigate("/login");
    });
    return () => unsubscribe();
  }, [navigate]);

  const loadSummary = useCallback(async () => {
    if (!user) return;
    try {
      setIsLoading(true);
      const token = await user.getIdToken();
      const data = await fetchReferralSummary(token);
      setSummary(data);
    } catch (error) {
      console.error("Error loading referral summary:", error);
      toast.error("Could not load your referral details");
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  const handleCopy = async () => {
    if (!summary?.code) return;
    try {
      await navigator.clipboard.writeText(summary.code);
      setCopied(true);
      toast.success("Code copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Could not copy code");
    }
  };

  const handleShareWhatsApp = () => {
    if (!summary?.code) return;
    const message =
      `Use my Teco referral code ${summary.code} on your first order — ` +
      `we'll both get KES ${REFEREE_REWARD} credit once you complete it!`;
    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, "_blank");
  };

  const handleApplyCode = async (e) => {
    e.preventDefault();
    if (!codeInput.trim() || !user) return;
    setIsApplying(true);
    try {
      const token = await user.getIdToken();
      const result = await applyReferralCode(codeInput.trim(), token);
      if (result.success) {
        toast.success(result.message);
        setCodeInput("");
        await loadSummary();
      } else {
        toast.error(result.message || "Could not apply code");
      }
    } catch (error) {
      console.error("Error applying referral code:", error);
      toast.error(error.message || "Could not apply code");
    } finally {
      setIsApplying(false);
    }
  };

  if (authLoading || (isLoading && !summary)) return <SkeletonPage />;
  if (!user) return null;

  return (
    <div style={{ fontFamily: FONT }}>
      <Seo {...generateNoIndexSEO("Refer & Earn")} />
      <Container className="py-3 px-3 px-md-4" style={{ maxWidth: 600 }}>

        {/* ── Header ── */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20, paddingTop: 4 }}>
          <IconBtn onClick={() => navigate(-1)}>
            <ArrowLeft size={17} color="#1C1C1E" />
          </IconBtn>
          <h1 style={{ margin: 0, fontSize: "1.125rem", fontWeight: 700, color: "#1C1C1E", letterSpacing: "-0.025em" }}>
            Refer &amp; Earn
          </h1>
        </div>

        {/* ── Code + share card ── */}
        <div
          style={{
            background: "linear-gradient(135deg, #007AFF 0%, #005ecb 100%)",
            borderRadius: 18,
            padding: "22px 20px",
            marginBottom: 16,
            color: "#fff",
          }}
        >
          <div style={{ fontSize: "0.8125rem", fontWeight: 500, opacity: 0.85, marginBottom: 4 }}>
            Your referral code
          </div>
          <div style={{ fontSize: "1.75rem", fontWeight: 800, letterSpacing: "0.08em", marginBottom: 16 }}>
            {summary?.code ?? "——————"}
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button
              onClick={handleCopy}
              style={{
                flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 7,
                padding: "11px", borderRadius: 12, border: "none",
                background: "rgba(255,255,255,0.16)", color: "#fff",
                fontSize: "0.875rem", fontWeight: 600, cursor: "pointer", fontFamily: FONT,
              }}
            >
              {copied ? <ClipboardCheck size={15} /> : <Clipboard size={15} />}
              {copied ? "Copied" : "Copy code"}
            </button>
            <button
              onClick={handleShareWhatsApp}
              style={{
                flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 7,
                padding: "11px", borderRadius: 12, border: "none",
                background: "#fff", color: "#007AFF",
                fontSize: "0.875rem", fontWeight: 600, cursor: "pointer", fontFamily: FONT,
              }}
            >
              <Whatsapp size={15} />
              Share
            </button>
          </div>
        </div>

        {/* ── Stats ── */}
        <Card style={{ display: "flex", alignItems: "center" }}>
          <StatBlock value={formatCurrency(summary?.walletBalance ?? 0)} label="Wallet credit" />
          <div style={{ width: 1, height: 34, background: "rgba(60,60,67,0.1)" }} />
          <StatBlock value={summary?.referralsCount ?? 0} label="Friends referred" />
          <div style={{ width: 1, height: 34, background: "rgba(60,60,67,0.1)" }} />
          <StatBlock value={formatCurrency(summary?.rewardsEarned ?? 0)} label="Total earned" />
        </Card>

        {summary?.walletBalance > 0 && (
          <p style={{ margin: "-8px 0 16px", fontSize: "0.78rem", color: "rgba(60,60,67,0.5)", textAlign: "center" }}>
            Your credit is applied automatically at checkout.
          </p>
        )}

        {/* ── Enter a friend's code ── */}
        {summary?.canApplyCode && (
          <Card>
            <SectionLabel>Have a friend's code?</SectionLabel>
            <Form onSubmit={handleApplyCode} style={{ display: "flex", gap: 10 }}>
              <Form.Control
                type="text"
                placeholder="Enter code"
                value={codeInput}
                onChange={(e) => setCodeInput(e.target.value.toUpperCase())}
                maxLength={12}
                style={{
                  flex: 1, backgroundColor: "rgba(118,118,128,0.1)", border: "1px solid transparent",
                  borderRadius: 10, padding: "10px 14px", fontSize: "0.9375rem",
                  letterSpacing: "0.04em", color: "#1c1c1e",
                }}
              />
              <button
                type="submit"
                disabled={isApplying || !codeInput.trim()}
                style={{
                  padding: "0 18px", borderRadius: 10, border: "none",
                  background: "#007AFF", color: "#fff", fontSize: "0.875rem", fontWeight: 600,
                  cursor: isApplying ? "not-allowed" : "pointer",
                  opacity: isApplying || !codeInput.trim() ? 0.6 : 1,
                  fontFamily: FONT,
                }}
              >
                {isApplying ? <Spinner size="sm" animation="border" /> : "Apply"}
              </button>
            </Form>
            <p style={{ margin: "10px 0 0", fontSize: "0.75rem", color: "rgba(60,60,67,0.5)" }}>
              Only available before your first order.
            </p>
          </Card>
        )}

        {/* ── How it works ── */}
        <div style={{ marginBottom: 8 }}>
          <SectionLabel>How it works</SectionLabel>
        </div>
        <Card style={{ padding: "4px 20px" }}>
          <StepRow
            Icon={PersonPlusFill}
            title="Share your code"
            body="Send it to a friend who hasn't ordered from Teco yet — via WhatsApp, text, or in person."
          />
          <StepRow
            Icon={BagCheckFill}
            title="They complete their first order"
            body="Your friend enters your code from their Account page, then places and completes an order."
          />
          <StepRow
            Icon={GiftFill}
            title="You both get credited"
            body={`You get KES ${REFERRER_REWARD}, they get KES ${REFEREE_REWARD} — added straight to your wallet.`}
          />
          <StepRow
            Icon={Wallet2}
            title="Spend it automatically"
            body="Wallet credit is applied to your next order at checkout — no code needed."
            last
          />
        </Card>

        <div
          style={{
            display: "flex", alignItems: "flex-start", gap: 8,
            padding: "12px 16px", marginBottom: 40,
            background: "rgba(52,199,89,0.07)", borderRadius: 12,
          }}
        >
          <CheckCircleFill size={13} color="#34C759" style={{ marginTop: 2, flexShrink: 0 }} />
          <p style={{ margin: 0, fontSize: "0.78rem", color: "rgba(60,60,67,0.65)", lineHeight: 1.5 }}>
            Each account can use one referral code, and only before its first order. Rewards are credited
            once your friend's order is complete — there's nothing else you need to do.
          </p>
        </div>

      </Container>
    </div>
  );
};

export default Referrals;
